import { Injectable, OnDestroy } from '@angular/core';
import {
	BehaviorSubject,
	EMPTY,
	Observable,
	OperatorFunction,
	Subject,
	concatMap,
	distinctUntilChanged,
	filter,
	first,
	pipe,
	switchMap,
	take,
	takeUntil,
	tap,
	timer
} from 'rxjs';

import { LightstreamerClient, Subscription } from 'lightstreamer-client-web';

import { LsConstants } from '@easy-lib/constants/ls.constants';
import { AccountService } from '@easy-lib/modules/account-activity/services/account.service';
import { EasyEnvironment } from '@easy-lib/modules/shared/models/environment';
import {
	ConnectionState,
	ILsData,
	ISubscriptionModel,
	LsSchemaType,
	SubscriptionChangeType,
	SubscriptionIsSnapshot,
	SubscriptionMode
} from '@easy-lib/modules/shared/models/ls.model';
import { timeout, unloadWindowEvent } from '@easy-lib/tools/event-tool';
import { guid } from '@easy-lib/tools/primptives-tool/string-tool';
import { GtagService } from './gtag.service';
// eslint-disable-next-line @typescript-eslint/ban-types

@Injectable({
	providedIn: 'root'
})
export class LsService implements OnDestroy {
	private lsClient: any;
	private subs = [];
	private connectionState: ConnectionState = ConnectionState.Disconnected;
	private connectionStateSubject = new BehaviorSubject<ConnectionState>(this.connectionState);
	connectionState$ = this.connectionStateSubject.asObservable();
	lsStableState$ = this.connectionState$.pipe(first(state => state === ConnectionState.Connected));
	private resetLsManuallySubject = new BehaviorSubject<boolean>(false);
	resetLsManually$ = this.resetLsManuallySubject.asObservable();
	lsConnectedSubject = new BehaviorSubject<boolean>(false);
	lsConnected$ = this.lsConnectedSubject.asObservable();
	constructor(
		private environment: EasyEnvironment,
		private accountService: AccountService,
		private gtagSevice: GtagService
	) {
		unloadWindowEvent(this?.unsubscribeAllAndDisconnect);
		if (!environment.production) {
			setInterval(() => {
				this.getListOfSubscriptions();
			}, 10000);
		}
		// this.testLS();
	}

	start(): any {
		this.lsClient = new LightstreamerClient(this.environment.setting.lsUrl, 'lsadapter-conf');
		let customerIsin = this.accountService?.userAccountInfo?.value?.customerIsin;
		if (this.environment.setting.lsUrl.includes('stage')) {
			if (customerIsin === '11290080931413' || customerIsin === '11290069635676') {
				customerIsin = '11290937779334';
			} else if (customerIsin === '11290011810777' || customerIsin === '11290222222220') {
				customerIsin = '11290859862836';
			}
		}
		this.lsClient.connectionDetails.setUser(customerIsin);
		this.lsClient.connectionOptions.setCookieHandlingRequired(true);
		this.lsClient.connectionOptions.setEarlyWSOpenEnabled(false);
		this.lsClient.connect();
		this.lsClient.addListener({
			onServerError: () => {
				this.changeConnectionState(ConnectionState.NotConnected);
				if (!this.environment.production) {
					// console.log(555, err);
				}
			},
			onStatusChange: status => {
				if (!this.environment.production) {
					// console.log(status);
				}
				try {
					const st = status.split(':');
					if (st.length > 0) {
						if (st[0] === 'CONNECTED') {
							this.changeConnectionState(ConnectionState.Connected);
							this.gtagSevice.sendGtagEvent('network_connectivity', 'network_status', 'Connected');
						} else if (st[0] === 'CONNECTING') {
							this.changeConnectionState(ConnectionState.Connecting);
							this.gtagSevice.sendGtagEvent('network_connectivity', 'network_status', 'Connecting');
						} else if (st[0] === 'DISCONNECTED') {
							this.changeConnectionState(ConnectionState.Disconnected);
							this.gtagSevice.sendGtagEvent('network_connectivity', 'network_status', 'Disconnected');
						} else {
							this.changeConnectionState(ConnectionState.Unknown);
							this.gtagSevice.sendGtagEvent('network_connectivity', 'network_status', 'Unknown');
						}
					}
				} catch (e) {
					if (!this.environment.production) {
						// console.log(e);
					}
					this.changeConnectionState(ConnectionState.Unknown);
				}
			}
		});
	}

	private changeConnectionState(state: ConnectionState) {
		this.connectionState = state;
		this.connectionStateSubject.next(state);
		if (state === ConnectionState.Connected) {
			this.lsConnectedSubject.next(true);
		} else this.lsConnectedSubject.next(false);
	}

	ngOnDestroy(): void {
		this.disconnect();
	}
	unsubscribeAllAndDisconnect() {
		try {
			this?.unsubscribeAll();
			this?.disconnect();
		} catch (error) {
			// console.log(error);
		}
	}
	private disconnect(): void {
		this.lsConnectedSubject.next(false);
		this.resetLsManuallySubject.next(true);
		this.lsClient?.disconnect();
	}
	resetLsConnection() {
		this.disconnect();
		this.start();
	}

	private subscribeToLsBySchema(data: {
		schemaType: LsSchemaType;
		schema?: any;
		elementId?: string;
		needSnapShot?: boolean;
	}): Observable<ILsData> {
		const subscriptionModel = this.getSubscriptionModelBySchema({
			schemaType: data.schemaType,
			elementId: data.elementId,
			schema: data.schema,
			needSnapShot: data.needSnapShot
		});
		return this.subscriptionToItems(subscriptionModel, data.elementId, data.schemaType);
	}

	private getSubscriptionModelBySchema(data: {
		schemaType: LsSchemaType;
		schema?: any;
		elementId?: string;
		needSnapShot?: boolean;
	}): ISubscriptionModel {
		const topic = LsConstants.LsSchemasTopic.find(a => a.schema === data.schemaType).topic;
		const subscriptionModel: ISubscriptionModel = {
			mode:
				LsConstants.RawModeSchemas.indexOf(data.schemaType) > -1
					? SubscriptionMode.Raw
					: SubscriptionMode.Merge,
			adaptername: LsConstants.LsAdapterName.find(
				a => a.topic === LsConstants.LsSchemasTopic.find(a => a.schema === data.schemaType).topic
			).value,
			schema: data.schema || this.getSchema(LsConstants.LsSchemas.find(a => a.type === data.schemaType).schema),
			issnapshot:
				LsConstants.SnapshotSchemas.indexOf(data.schemaType) > -1
					? SubscriptionIsSnapshot.Yes
					: SubscriptionIsSnapshot.No,
			items: [topic + (data.elementId ? ':' + data.elementId : '')],
			changeType:
				LsConstants.FullModeChangeSchemas.indexOf(data.schemaType) > -1
					? SubscriptionChangeType.Full
					: SubscriptionChangeType.Field
		};
		if (data.needSnapShot !== null && data.needSnapShot !== undefined) {
			subscriptionModel.issnapshot = data.needSnapShot ? SubscriptionIsSnapshot.Yes : SubscriptionIsSnapshot.No;
		}
		return subscriptionModel;
	}

	private getSchema(schema: any): string[] {
		return Object.keys(schema).map(key => schema[key]);
	}
	private unsubscribeAll() {
		try {
			const subs = this?.lsClient?.getSubscriptions();
			subs?.forEach(sub => {
				sub?.unsubscribe();
			});
		} catch (error) {
			// console.log(error);
		}
	}
	private subscriptionToItems(
		subscriptionModel: ISubscriptionModel,
		elementId?: string,
		schemaType?: LsSchemaType
	): Observable<ILsData> {
		const sub = new Subscription(subscriptionModel.mode, subscriptionModel.items, subscriptionModel.schema);
		sub.setDataAdapter(subscriptionModel.adaptername);
		sub.setRequestedSnapshot(subscriptionModel.issnapshot);
		const changeFieldSource = new Subject<ILsData>();
		const changeField$ = changeFieldSource.asObservable();
		const subId = guid();
		sub.addListener({
			onItemUpdate: updateInfo => {
				try {
					if (subscriptionModel.changeType === SubscriptionChangeType.Field) {
						updateInfo.forEachChangedField((fieldName, fieldPos, val) => {
							const data: ILsData = {
								inst: elementId ?? '',
								name: fieldName,
								pos: fieldPos,
								val,
								subId
							};
							if (data.val || data.val == 0) changeFieldSource.next(data);
						});
					} else {
						const d = {};
						subscriptionModel.schema.forEach(element => {
							d[element] = updateInfo.getValue(element);
						});
						const data: ILsData = {
							inst: elementId ?? '',
							name: '',
							pos: 0,
							val: d,
							subId
						};
						// if (this.connectionStateSubject.value == ConnectionState.Connected) {
						changeFieldSource.next(data);
						// }
					}
				} catch (e) {
					// console.log(e);
				}
			}
		});

		this.lsClient.subscribe(sub);
		this.subs.push({ elementId, schemaType, subscription: sub, subId });
		return changeField$;
	}

	private getListOfSubscriptions() {
		const subs = this.lsClient?.getSubscriptions();
		// console.log(subs);
		if (subs) {
			// console.log(
			// 	'current subs:',
			// 	subs.map(x => x.requestParams.LS_group)
			// );
			// .map(sub => [sub?.Om?.LS_group, sub?.Om?.LS_data_adapter, 'snapshot:', sub?.Om?.LS_snapshot].join(', '));
		}
	}

	unsubscribeSchema(type: LsSchemaType, item?: string): void {
		const subs = this.subs.filter(a => a.schemaType === type && a.elementId === (item ?? a.elementId));

		if (subs) {
			subs.forEach(sub => {
				this.lsClient.unsubscribe(sub.subscription);
			});
			this.subs = this.subs.filter(a => !(a.schemaType === type && a.elementId === (item ?? a.elementId)));
		}
	}

	isSubscriptionAvailable(type: LsSchemaType, item?: string): boolean {
		return this.subs.some(a => a.schemaType === type && a.elementId === (item ?? a.elementId));
	}

	unsubscribeSchemaTypeExceptSelectedItems(type: LsSchemaType, items: string[]): void {
		const subs = this.subs.filter(a => a.schemaType === type && !items.includes(a.elementId));
		if (subs?.length) {
			subs.forEach(sub => {
				this.lsClient.unsubscribe(sub.subscription);
			});
			this.subs = this.subs.filter(a => !subs.map(b => b.subId).includes(a.subId));
		}
	}
	unsubscribeSchemaTypeAndIds(type: LsSchemaType, ids: string[]): void {
		const subs = this.subs.filter(a => a.schemaType === type && ids.includes(a.elementId));
		if (subs?.length) {
			subs.forEach(sub => {
				this.lsClient.unsubscribe(sub.subscription);
			});
			this.subs = this.subs.filter(a => !subs.map(b => b.subId).includes(a.subId));
		}
	}
	unsubscribeSchemaById(subId): void {
		const sub = this.subs.find(a => a.subId === subId);
		if (sub) {
			this.lsClient.unsubscribe(sub.subscription);
			this.subs = this.subs.filter(a => !(a.subId === subId));
		}
	}
	unsubscribeSchemaByIds(subIds: string[]): void {
		subIds?.forEach(subId => this.unsubscribeSchemaById(subId));
	}

	waitForStableConnection(): Observable<boolean> {
		return this.lsConnectedSubject.pipe(first(state => state));
	}

	subscribeToLs(schemaType: LsSchemaType, elementId?: string, needSnapShot = null) {
		return this.lsConnectedSubject.pipe(
			first(state => state),
			concatMap(() => this.subscribeToLsBySchema({ schemaType, elementId, needSnapShot }))
		);
	}

	subscribeToLsIfNotAvailable(schemaType: LsSchemaType, elementIds: string[], func: (res) => void) {
		elementIds.forEach(id => {
			if (!this.isSubscriptionAvailable(schemaType, id))
				this.subscribeToLs(schemaType, id).subscribe(res => {
					func(res);
				});
		});
	}

	subscribeToLsWithDynamicSchema(schemaType: LsSchemaType, dynamicSchema: any, elementId?: string) {
		return this.lsConnectedSubject.pipe(
			first(state => state),
			concatMap(() => this.subscribeToLsBySchema({ schemaType, elementId, schema: dynamicSchema }))
		);
	}

	createDynamicSchema(...props: string[]) {
		return [...props];
	}

	subscribeToLs2<T>(
		func: (a: T | ILsData) => void,
		schemaType: LsSchemaType,
		elementId?: string,
		operatorFunction?: OperatorFunction<ILsData, T>
	) {
		const obs$ = this.getObservableWithCondition(schemaType, elementId, operatorFunction);
		obs$.subscribe(a => func(a));
		this.resetLsManually$.pipe(filter(res => res)).subscribe(() => {
			const obs$ = this.getObservableWithCondition(schemaType, elementId, operatorFunction);
			obs$.subscribe(a => func(a));
		});
	}
	getObservableWithCondition<T>(
		schemaType: LsSchemaType,
		elementId?: string,
		operatorFunction?: OperatorFunction<ILsData, T>
	) {
		let obs$ = this.subscribeToLs(schemaType, elementId);
		if (operatorFunction) {
			obs$ = obs$.pipe(pipe.apply(operatorFunction));
		}
		return obs$;
	}

	//------------------TODO---REFACTOR---------------
	testLS() {
		timeout(() => {
			// console.log('/-----------------Disconnected-------------------/');
			this.changeConnectionState(ConnectionState.Disconnected);
			// this.connectionStateSubject.next(ConnectionState.Disconnected);
		}, 10000);
		timeout(() => {
			// console.log('/------------------Connected------------------/');
			this.changeConnectionState(ConnectionState.Connected);
			// this.connectionStateSubject.next(ConnectionState.Connected);
		}, 25000);
		timeout(() => {
			// console.log('/-------------------Disconnected-----------------/');
			this.changeConnectionState(ConnectionState.Disconnected);
			// this.connectionStateSubject.next(ConnectionState.Disconnected);
		}, 30000);
		timeout(() => {
			// console.log('/-------------------Connected-----------------/');
			this.changeConnectionState(ConnectionState.Connected);
			// this.connectionStateSubject.next(ConnectionState.Connected);
		}, 40000);
	}
	//TODO: for cancelation token
	callFunctionUntilDestroy(input: Partial<ILSCallBackOption>): Observable<number> {
		const { func, startDue, intervalDuration, count } = new LSCallBackOption(input);
		const operation = count ? take<number>(count) : tap<number>();
		return timer(startDue, intervalDuration).pipe(
			operation,
			tap(() => func())
		);
	}

	callFunctionUntilLsDataReceived(input: Partial<ILSCallBackOption>): Observable<number> {
		const { func, startDue, intervalDuration, count } = new LSCallBackOption(input);
		const operation = count ? take<number>(count) : tap<number>();
		return timer(startDue, intervalDuration).pipe(
			operation,
			tap(() => func())
		);
	}
	connectionFinalState$ = this.connectionState$.pipe(
		filter(state => [ConnectionState.Connected, ConnectionState.Disconnected].includes(state)),
		distinctUntilChanged()
	);
	callFunctionWhenLsIsNotConnected(
		func$: () => Observable<any>,
		extra?: Partial<ILSCallBackOption>
	): Observable<number> {
		const { func, startDue, intervalDuration, count } = new LSCallBackOption(extra);
		const takeOparation = count ? take<number>(count) : takeUntil<number>(this.lsStableState$);
		const switchMapOparation = switchMap(() => (func$ ? func$() : EMPTY));
		return this.lsConnected$.pipe(
			distinctUntilChanged(),
			filter(state => !state),
			switchMap(() =>
				timer(startDue, intervalDuration).pipe(
					takeOparation,
					tap(() => func?.()),
					switchMapOparation
				)
			)
		);
	}
	//------------------------------------------
}
export interface ILSCallBackFunction {
	func: () => void;
}
export interface ILSCallBackFunction$ {
	func$: () => Observable<any>;
}
export interface ILSCallBackOption extends ILSCallBackFunction {
	startDue: number;
	intervalDuration: number;
	count: number;
}
class LSCallBackOption implements ILSCallBackOption {
	func: () => void;
	startDue: number;
	intervalDuration: number;
	count: number;
	constructor(input: Partial<LSCallBackOption>) {
		this.func = input?.func;
		this.startDue = input?.startDue ?? 0;
		this.intervalDuration = input?.intervalDuration ?? 7000;
		this.count = input?.count;
	}
}
